import * as Api from '@ViewModels';
import { VmUtils } from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css, StyleSheet } from 'aphrodite';
import { addDays, format, startOfDay } from 'date-fns';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { useActionItemComposer, useUserSession } from '../../../../models/hooks/appStateHooks';
import { actionItemQueryKeys } from '../../../../queries/ActionItem/actionItemQueryKeys';
import { ICreateActionItemInput } from '../../../../queries/ActionItem/useActionItemMutations';
import { useCompanyAutocomplete } from '../../../../queries/Company/useCompanyAutocomplete';
import { useContactAutocomplete } from '../../../../queries/Contact/useContactAutocomplete';
import { useUserAutocompleteQuery } from '../../../../queries/User/useUserAutocompleteQuery';
import { Button } from '../../../components-v2/Button';
import { Calendar } from '../../../components-v2/Calendar';
import {
	Dropdown,
	DropdownContent,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownTrigger,
} from '../../../components-v2/Dropdown';
import { Input } from '../../../components-v2/Input';
import { Pill } from '../../../components-v2/Pill';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { THEME } from '../../../components-v2/Theme/theme';
import { CALENDAR_ACTION_ITEMS_QUERY_KEY } from '../../../components/calendar/hooks/calendarActionItemsQueryKey';
import { AssigneeIcon } from '../../../components/svgs/icons/AssigneeIcon';
import { BuildingIcon } from '../../../components/svgs/icons/BuildingIcon';
import { CalendarIcon } from '../../../components/svgs/icons/CalendarIcon';
import { ExpandIcon } from '../../../components/svgs/icons/ExpandIcon';
import { PlusIcon2 } from '../../../components/svgs/icons/PlusIcon';
import { PrivateVisibilityIcon } from '../../../components/svgs/icons/PrivateVisibilityIcon';
import { PublicVisibilityIcon } from '../../../components/svgs/icons/PublicVisibilityIcon';
import { toActionItemContent } from '../utils/actionItemText';
import { PRIORITY_LABELS } from '../utils/priority';
import {
	extractPriority,
	getActiveMention,
	IActiveMention,
	parseQuickAdd,
	removeMention,
	replaceMention,
} from '../utils/quickAddParser';

export interface IQuickAddControl {
	/** Append a natural-language phrase (like "tomorrow") to the draft and focus the input. */
	appendPhrase: (phrase: string) => void;
}

interface IProps {
	inputRef: React.RefObject<HTMLInputElement>;
	controlRef?: React.MutableRefObject<IQuickAddControl | null>;
	onCreate: (input: ICreateActionItemInput) => void;
	isCreating?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

type Suggestion =
	| { kind: 'contact'; entity: Api.IContact }
	| { kind: 'company'; entity: Api.ICompany }
	| { kind: 'user'; entity: Api.IUser };

const CONTACT_SUGGESTIONS = 5;
const COMPANY_SUGGESTIONS = 3;
const USER_SUGGESTIONS = 6;

const VISIBILITY_OPTIONS: { value: Api.ResourceVisibility; label: string }[] = [
	{ value: 'all', label: 'All employees' },
	{ value: 'admin', label: 'Admins only' },
];

function escapeRegExp(value: string) {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function suggestionLabel(suggestion: Suggestion): string {
	return suggestion.kind === 'company' ? (suggestion.entity.companyName ?? '') : getDisplayName(suggestion.entity);
}

function suggestionDetail(suggestion: Suggestion): string | undefined {
	if (suggestion.kind === 'company') {
		return 'Company';
	}
	return suggestion.entity.primaryEmail?.value;
}

function insertAtCaret(text: string, caret: number, token: string): { text: string; caret: number } {
	const before = text.slice(0, caret);
	const after = text.slice(caret);
	const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
	const inserted = `${needsSpaceBefore ? ' ' : ''}${token}`;
	return { text: `${before}${inserted}${after}`, caret: before.length + inserted.length };
}

export const QuickAddBar: React.FC<IProps> = ({ inputRef, controlRef, onCreate, isCreating, impersonationContext }) => {
	const userSession = useUserSession();
	const actionItemComposer = useActionItemComposer();
	const queryClient = useQueryClient();
	const defaultVisibility = VmUtils.getDefaultVisibility(userSession.user);

	const [text, setText] = React.useState('');
	const [caret, setCaret] = React.useState(0);
	const [contacts, setContacts] = React.useState<Api.IContact[]>([]);
	const [companies, setCompanies] = React.useState<Api.ICompany[]>([]);
	const [assignee, setAssignee] = React.useState<Api.IUser | null>(null);
	const [visibility, setVisibility] = React.useState<Api.ResourceVisibility>(defaultVisibility);
	const [highlight, setHighlight] = React.useState(0);

	const parsed = React.useMemo(() => parseQuickAdd(text), [text]);
	const mention = React.useMemo(() => getActiveMention(text, caret), [text, caret]);
	const mentionQuery = mention?.query.trim() ?? '';
	const mentionActive = !!mention && mentionQuery.length > 0;
	const entityMentionActive = mentionActive && mention?.kind === 'contact';

	const contactResults = useContactAutocomplete({
		enabled: entityMentionActive,
		fragment: mentionQuery,
		pageSize: CONTACT_SUGGESTIONS,
		impersonationContext,
	});
	const companyResults = useCompanyAutocomplete({
		enabled: entityMentionActive,
		fragment: mentionQuery,
		pageSize: COMPANY_SUGGESTIONS,
	});
	const userResults = useUserAutocompleteQuery({
		enabled: mentionActive && mention?.kind === 'user',
		fragment: mentionQuery,
		pageSize: USER_SUGGESTIONS,
	});

	const suggestions: Suggestion[] = React.useMemo(() => {
		if (!mentionActive || !mention) {
			return [];
		}
		if (mention.kind === 'user') {
			return (userResults.data?.values ?? []).map(entity => ({ kind: 'user', entity }));
		}
		const contactSuggestions: Suggestion[] = (contactResults.data?.pages[0]?.values ?? [])
			.filter(contact => !contacts.some(c => c.id === contact.id))
			.slice(0, CONTACT_SUGGESTIONS)
			.map(entity => ({ kind: 'contact', entity }));
		const companySuggestions: Suggestion[] = (companyResults.data?.pages[0]?.values ?? [])
			.filter(company => !companies.some(c => c.id === company.id))
			.slice(0, COMPANY_SUGGESTIONS)
			.map(entity => ({ kind: 'company', entity }));
		return [...contactSuggestions, ...companySuggestions];
	}, [mentionActive, mention, contactResults.data, companyResults.data, userResults.data, contacts, companies]);

	React.useEffect(() => {
		setHighlight(0);
	}, [mentionQuery, mention?.kind]);

	const focusInput = () => window.requestAnimationFrame(() => inputRef.current?.focus());

	const pickSuggestion = (picked: Suggestion, activeMention: IActiveMention) => {
		if (picked.kind === 'contact') {
			setContacts(previous =>
				previous.some(c => c.id === picked.entity.id) ? previous : [...previous, picked.entity]
			);
		} else if (picked.kind === 'company') {
			setCompanies(previous =>
				previous.some(c => c.id === picked.entity.id) ? previous : [...previous, picked.entity]
			);
		} else {
			setAssignee(picked.entity);
		}
		const nextText =
			picked.kind === 'user'
				? removeMention(text, activeMention)
				: replaceMention(text, activeMention, suggestionLabel(picked));
		setText(nextText);
		setCaret(nextText.length);
		focusInput();
	};

	const [explicitDueDate, setExplicitDueDate] = React.useState<Date | null>(null);
	const [datePickerOpen, setDatePickerOpen] = React.useState(false);
	const dueDate = explicitDueDate ?? parsed.dueDate;
	const hasDueTime = explicitDueDate ? false : parsed.hasDueTime;

	const reset = () => {
		setText('');
		setCaret(0);
		setContacts([]);
		setCompanies([]);
		setAssignee(null);
		setVisibility(defaultVisibility);
		setExplicitDueDate(null);
	};

	const pickDate = (day: Date | undefined) => {
		setExplicitDueDate(day ? startOfDay(day) : null);
		setDatePickerOpen(false);
		focusInput();
	};

	const hasAnyInput = !!text || contacts.length > 0 || companies.length > 0 || !!assignee;

	const buildInput = (): ICreateActionItemInput => {
		const title = parsed.title.trim();
		return {
			plainText: title,
			content: toActionItemContent(title),
			dueDate: dueDate ? dueDate.toISOString() : null,
			hasDueTime,
			priority: parsed.priority,
			assignee,
			contacts,
			companies,
			visibility,
		};
	};

	const submit = () => {
		if (!parsed.title.trim()) {
			return;
		}
		onCreate(buildInput());
		reset();
	};

	const openFullEditor = () => {
		const input = buildInput();
		const model: Api.IActionItem = {
			content: input.plainText ? input.content : undefined,
			dueDate: input.dueDate ?? undefined,
			hasDueTime: input.hasDueTime,
			priority: input.priority ?? undefined,
			assignee: input.assignee ? ({ id: input.assignee.id } as Api.IUser) : undefined,
			visibility: input.visibility,
			referencedEntities: {
				companies: companies.map(company => ({ entity: company, method: Api.RichContentReferenceMethod.Explicit })),
				contacts: contacts.map(contact => ({ entity: contact, method: Api.RichContentReferenceMethod.Explicit })),
				users: [],
			},
		};
		const viewModel = new Api.ActionItemViewModel(userSession, model);
		actionItemComposer.show(viewModel, (_saved, result) => {
			if (result === Api.RichContentComposerResult.None) {
				return;
			}
			queryClient.invalidateQueries({ queryKey: actionItemQueryKeys.all });
			queryClient.invalidateQueries({ queryKey: [CALENDAR_ACTION_ITEMS_QUERY_KEY] });
			reset();
		});
	};

	React.useEffect(() => {
		if (!controlRef) {
			return;
		}
		controlRef.current = {
			appendPhrase: phrase => {
				const base = text.trim();
				const next = phrase ? (base ? `${base} ${phrase}` : phrase) : base;
				setText(next);
				setCaret(next.length);
				window.requestAnimationFrame(() => inputRef.current?.focus());
			},
		};
		return () => {
			controlRef.current = null;
		};
	});

	const insertToken = (token: string) => {
		const next = insertAtCaret(text, caret, token);
		setText(next.text);
		setCaret(next.caret);
		window.requestAnimationFrame(() => {
			const input = inputRef.current;
			if (input) {
				input.focus();
				input.setSelectionRange(next.caret, next.caret);
			}
		});
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (mentionActive && suggestions.length > 0) {
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				setHighlight(index => (index + 1) % suggestions.length);
				return;
			}
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				setHighlight(index => (index - 1 + suggestions.length) % suggestions.length);
				return;
			}
			if ((event.key === 'Enter' || event.key === 'Tab') && mention) {
				event.preventDefault();
				pickSuggestion(suggestions[highlight] ?? suggestions[0], mention);
				return;
			}
		}
		if (event.key === 'Enter') {
			event.preventDefault();
			submit();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			if (hasAnyInput) {
				reset();
			} else {
				inputRef.current?.blur();
			}
		}
	};

	const removeDate = () => {
		if (explicitDueDate) {
			setExplicitDueDate(null);
			return;
		}
		if (!parsed.dateText) {
			return;
		}
		const pattern = new RegExp(`\\s*(?:\\b(?:on|at|by|due|for)\\s+)?${escapeRegExp(parsed.dateText)}`, 'i');
		setText(text.replace(pattern, ' ').replace(/\s+/g, ' ').trim());
	};

	const removePriority = () => setText(extractPriority(text).text);

	const visibilityLabel = VISIBILITY_OPTIONS.find(option => option.value === visibility)?.label ?? 'Group';
	const isPublic = visibility === 'all';
	const hasChips =
		!!dueDate || !!parsed.priority || contacts.length > 0 || companies.length > 0 || !!assignee || !isPublic;

	return (
		<div className={css(styleSheet.container)} data-testid='quick-add'>
			<Popover open={mentionActive && suggestions.length > 0}>
				<PopoverAnchor asChild>
					<div className={css(styleSheet.inputRow)}>
						<span className={css(styleSheet.plusBadge)} aria-hidden>
							<PlusIcon2 className={css(styleSheet.plusIcon)} width={14} height={14} />
						</span>
						<div className={css(styleSheet.inputWrap)}>
							<Input
								ref={inputRef}
								aria-label='Add an action item'
								placeholder='Add an action item… try "@Bob renewal call Tue 3pm !high"'
								styles={[styleSheet.input]}
								value={text}
								disabled={isCreating}
								onChange={event => {
									setText(event.currentTarget.value);
									setCaret(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
								}}
								onKeyUp={event => setCaret(event.currentTarget.selectionStart ?? caret)}
								onClick={event => setCaret(event.currentTarget.selectionStart ?? caret)}
								onKeyDown={onKeyDown}
							/>
						</div>
						<div className={css(styleSheet.tools)}>
							<Button
								variant='ghost'
								size='icon'
								type='button'
								aria-label='Reference a contact or company'
								title='Reference a contact or company (@)'
								disabled={isCreating}
								onMouseDown={event => event.preventDefault()}
								onClick={() => insertToken('@')}
							>
								<span className={css(styleSheet.atSign)} aria-hidden>
									@
								</span>
							</Button>
							<Button
								variant='ghost'
								size='icon'
								type='button'
								aria-label='Assign to a teammate'
								title='Assign to a teammate (for @)'
								disabled={isCreating}
								onMouseDown={event => event.preventDefault()}
								onClick={() => insertToken('for @')}
							>
								<AssigneeIcon width={16} height={17} />
							</Button>
							<Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
								<PopoverTrigger asChild>
									<Button
										variant='ghost'
										size='icon'
										type='button'
										aria-label={dueDate ? `Due ${format(dueDate, 'EEE, MMM d')}` : 'Set a due date'}
										title='Set a due date'
										disabled={isCreating}
										styles={[!!dueDate && styleSheet.toolActive]}
									>
										<CalendarIcon />
									</Button>
								</PopoverTrigger>
								<PopoverContent align='end' styles={[styleSheet.datePicker]}>
									<div className={css(styleSheet.dateQuickPicks)}>
										<Button variant='outline' size='sm' onClick={() => pickDate(new Date())}>
											Today
										</Button>
										<Button variant='outline' size='sm' onClick={() => pickDate(addDays(new Date(), 1))}>
											Tomorrow
										</Button>
										<Button variant='outline' size='sm' onClick={() => pickDate(addDays(new Date(), 7))}>
											Next week
										</Button>
									</div>
									<Calendar mode='single' selected={dueDate ?? undefined} onSelect={day => pickDate(day)} />
								</PopoverContent>
							</Popover>
							<Dropdown>
								<DropdownTrigger
									render={
										<Button
											variant='ghost'
											size='icon'
											type='button'
											aria-label={`Visibility: ${visibilityLabel}`}
											title={`Visibility: ${visibilityLabel}`}
											disabled={isCreating}
										/>
									}
								>
									{isPublic ? <PublicVisibilityIcon /> : <PrivateVisibilityIcon />}
								</DropdownTrigger>
								<DropdownContent align='end'>
									<DropdownRadioGroup
										value={visibility}
										onValueChange={value => setVisibility(value as Api.ResourceVisibility)}
									>
										{VISIBILITY_OPTIONS.map(option => (
											<DropdownRadioItem key={option.value} value={option.value}>
												{option.label}
											</DropdownRadioItem>
										))}
									</DropdownRadioGroup>
								</DropdownContent>
							</Dropdown>
							<Button
								variant='ghost'
								size='icon'
								type='button'
								aria-label='Open full editor'
								title='Open full editor'
								disabled={isCreating}
								onClick={openFullEditor}
							>
								<ExpandIcon fill='currentColor' width={16} height={16} />
							</Button>
						</div>
						<Button size='sm' disabled={!parsed.title.trim() || isCreating} onClick={submit}>
							Add
						</Button>
					</div>
				</PopoverAnchor>
				<PopoverContent
					align='start'
					styles={[styleSheet.suggestions]}
					onOpenAutoFocus={event => event.preventDefault()}
					onCloseAutoFocus={event => event.preventDefault()}
				>
					<span className={css(styleSheet.suggestionsLabel)}>
						{mention?.kind === 'user' ? 'Assign to teammate' : 'Reference a contact or company'}
					</span>
					{suggestions.map((suggestion, index) => (
						<Button
							key={`${suggestion.kind}-${suggestion.entity.id}`}
							variant='ghost'
							size='sm'
							role='option'
							aria-selected={index === highlight}
							styles={[styleSheet.suggestion, index === highlight && styleSheet.suggestionHighlighted]}
							onMouseEnter={() => setHighlight(index)}
							onMouseDown={event => event.preventDefault()}
							onClick={() => mention && pickSuggestion(suggestion, mention)}
						>
							{suggestion.kind === 'company' ? (
								<BuildingIcon className={css(styleSheet.suggestionIcon)} width={16} height={15} />
							) : null}
							<span className={css(styleSheet.suggestionName)}>{suggestionLabel(suggestion)}</span>
							{suggestionDetail(suggestion) ? (
								<span className={css(styleSheet.suggestionEmail)}>{suggestionDetail(suggestion)}</span>
							) : null}
						</Button>
					))}
				</PopoverContent>
			</Popover>
			{hasChips ? (
				<div className={css(styleSheet.chips)} data-testid='quick-add-chips'>
					{dueDate ? (
						<Pill
							variant='tag'
							color='teal'
							label={format(dueDate, hasDueTime ? 'EEE, MMM d, h:mm a' : 'EEE, MMM d')}
							onRemove={removeDate}
						/>
					) : null}
					{contacts.map(contact => (
						<Pill
							key={contact.id}
							variant='tag'
							color='blue'
							label={getDisplayName(contact)}
							value={contact.id}
							onRemove={id => setContacts(previous => previous.filter(c => c.id !== id))}
						/>
					))}
					{companies.map(company => (
						<Pill
							key={company.id}
							variant='tag'
							color='green'
							label={company.companyName ?? ''}
							value={company.id}
							onRemove={id => setCompanies(previous => previous.filter(c => c.id !== id))}
						/>
					))}
					{assignee ? (
						<Pill
							variant='tag'
							color='purple'
							label={`For ${getDisplayName(assignee)}`}
							onRemove={() => setAssignee(null)}
						/>
					) : null}
					{parsed.priority ? (
						<Pill
							variant='tag'
							color={parsed.priority === 'High' ? 'red' : parsed.priority === 'Medium' ? 'orange' : 'gray'}
							label={PRIORITY_LABELS[parsed.priority]}
							onRemove={removePriority}
						/>
					) : null}
					{!isPublic ? (
						<Pill variant='tag' color='gray' label={visibilityLabel} onRemove={() => setVisibility('all')} />
					) : null}
				</div>
			) : null}
			<div className={css(styleSheet.hints)} aria-hidden>
				<span className={css(styleSheet.hint)}>
					<kbd className={css(styleSheet.kbd)}>@</kbd> contact or company
				</span>
				<span className={css(styleSheet.hint)}>
					<kbd className={css(styleSheet.kbd)}>for @</kbd> teammate
				</span>
				<span className={css(styleSheet.hint)}>
					<kbd className={css(styleSheet.kbd)}>due tomorrow 3pm</kbd> date or time
				</span>
				<span className={css(styleSheet.hint)}>
					<kbd className={css(styleSheet.kbd)}>!high</kbd> priority
				</span>
				<span className={css(styleSheet.hint)}>
					<kbd className={css(styleSheet.kbd)}>n</kbd> new
				</span>
				<span className={css(styleSheet.hint)}>
					<kbd className={css(styleSheet.kbd)}>/</kbd> search
				</span>
			</div>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px 0 ${THEME.spacing.md}px`,
	},
	inputRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px ${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		transition: 'border-color 120ms ease-out, box-shadow 120ms ease-out',
		':focus-within': {
			borderColor: THEME.colors.primary.main,
			boxShadow: `0 0 0 3px ${THEME.colors.primary.light}`,
		},
	},
	plusBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		borderRadius: '50%',
		backgroundColor: THEME.colors.primary.light,
		flexShrink: 0,
	},
	plusIcon: {
		color: THEME.colors.primary.main,
	},
	inputWrap: {
		flex: 1,
		minWidth: 0,
		position: 'relative',
	},
	input: {
		width: '100%',
		height: 40,
		fontSize: THEME.typography.fontSizes.md,
		border: 'none',
		boxShadow: 'none',
		backgroundColor: 'transparent',
		':focus': {
			boxShadow: 'none',
			outline: 'none',
		},
		':focus-visible': {
			boxShadow: 'none',
			outline: 'none',
		},
	},
	hints: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.md,
		paddingLeft: THEME.spacing.sm,
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	hint: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	kbd: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadiusSm,
		padding: '0 6px',
		fontFamily: 'inherit',
		fontSize: THEME.typography.fontSizes.xxs,
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.main,
		lineHeight: '18px',
	},
	tools: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		color: THEME.colors.icon.gray,
		flexShrink: 0,
	},
	atSign: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1,
	},
	suggestions: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 280,
		padding: THEME.spacing.xs,
	},
	suggestionsLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		textTransform: 'uppercase',
		letterSpacing: 0.4,
	},
	suggestion: {
		justifyContent: 'flex-start',
		gap: THEME.spacing.sm,
		width: '100%',
	},
	suggestionHighlighted: {
		backgroundColor: THEME.colors.hover.light,
	},
	suggestionIcon: {
		color: THEME.colors.icon.gray,
		flexShrink: 0,
	},
	suggestionName: {
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	suggestionEmail: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		...THEME.common.truncate,
	},
	toolActive: {
		color: THEME.colors.primary.main,
	},
	datePicker: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.sm,
	},
	dateQuickPicks: {
		display: 'flex',
		gap: THEME.spacing.xs,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
		paddingLeft: 26,
	},
});
